import React, { useState } from "react";
import { Badge, Button, Col, Form, Row, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaRegEdit, FaRupeeSign, FaReceipt } from "react-icons/fa";
import Loader from "../components/Loader";
import Message from "../components/Message";
import PaymentModal from "../components/PaymentModal";
import { useGetPaymentsQuery } from "../slices/paymentSlice";
import { useLazyGetOrdersQuery } from "../slices/OrdersSlice";
import { getCurrentDate, getDateOfLast } from "../utils/getCurrentDate";
import { formatAmount } from "../utils/commonUtils";

const buildQuery = (start, end) =>
  `paymentDate[gte]=${start}&paymentDate[lte]=${end}T23:59:59.999Z&sort=-updatedAt`;

const cardStyle = {
  backgroundColor: "#ffffff",
  border: "1px solid #e3ecf3",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(29, 95, 145, 0.08)",
};

const summaryChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  padding: "0.45rem 0.9rem",
  borderRadius: "14px",
  border: "1px solid #b7dbf7",
  background:
    "linear-gradient(to bottom right, #ffffff 0%, #d9ebf9 45%, #c2e5fc 100%)",
  color: "#1d5f91",
  fontWeight: "600",
  fontSize: "0.85rem",
};

const headStyle = {
  backgroundColor: "#f4f9fd",
  color: "#1d5f91",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  borderBottom: "2px solid #e3ecf3",
  padding: "0.6rem 0.75rem",
  whiteSpace: "nowrap",
};

const cellStyle = {
  padding: "0.55rem 0.75rem",
  verticalAlign: "middle",
  fontSize: "0.88rem",
};

const PaymentsHistoryScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);

  const [startDate, setStartDate] = useState(getDateOfLast(30));
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [apiQuery, setApiQuery] = useState(
    buildQuery(getDateOfLast(30), getCurrentDate())
  );

  const [editingPayment, setEditingPayment] = useState(null);
  const [openingInvoice, setOpeningInvoice] = useState(null);

  const navigate = useNavigate();

  const {
    data: payments,
    isLoading,
    error,
    refetch,
  } = useGetPaymentsQuery({ query: apiQuery, token: userInfo?.token });

  const [fetchOrders] = useLazyGetOrdersQuery();

  // Opens the order details page of the order this payment belongs to
  const handleInvoiceClick = async (invoiceNumber) => {
    if (!invoiceNumber || openingInvoice) return;
    try {
      setOpeningInvoice(invoiceNumber);
      const orders = await fetchOrders({
        query: `invoiceNumber=${encodeURIComponent(invoiceNumber)}`,
        token: userInfo?.token,
      }).unwrap();
      const order = Array.isArray(orders) ? orders[0] : orders;
      if (order) {
        navigate("/orderDetails", { state: { orderResponse: order } });
      } else {
        toast.error("No order found for this invoice");
      }
    } catch (err) {
      toast.error(err?.data?.error || "Unable to open order details");
    } finally {
      setOpeningInvoice(null);
    }
  };

  const paymentsList = Array.isArray(payments) ? payments : [];
  const totalPaid = paymentsList.reduce(
    (sum, payment) => sum + Number(payment?.paidAmount || 0),
    0
  );

  const handleGetPayments = () => {
    if (startDate > getCurrentDate()) {
      toast.error("Start date can not be greater than today");
      return;
    }
    if (startDate > endDate) {
      toast.error("Start date can not be greater than End date");
      return;
    }
    setApiQuery(buildQuery(startDate, endDate));
  };

  return (
    <div style={{ padding: "0.5rem 0.6rem" }}>
      {/* Filter card */}
      <div
        style={{ ...cardStyle, padding: "0.9rem 1rem", marginBottom: "0.8rem" }}
      >
        <Row className="align-items-end" style={{ rowGap: "0.6rem" }}>
          <Col xs={12} md={4}>
            <h5 className="mb-1">
              <strong style={{ color: "#1d5f91" }}>Payments History</strong>
            </h5>
            <small className="text-muted">
              Select a date range and click GET
            </small>
          </Col>
          <Col xs={6} sm={4} md={3}>
            <Form.Label
              className="mb-1"
              style={{ fontSize: "0.75rem", color: "#6c757d" }}
            >
              FROM
            </Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>
          <Col xs={6} sm={4} md={3}>
            <Form.Label
              className="mb-1"
              style={{ fontSize: "0.75rem", color: "#6c757d" }}
            >
              TO
            </Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>
          <Col xs={12} sm={4} md={2} className="d-grid">
            <Button
              className={`${userInfo?.themeColor}ThemeHeaderTop`}
              style={{ borderRadius: "8px", fontWeight: "600" }}
              onClick={handleGetPayments}
            >
              GET
            </Button>
          </Col>
        </Row>
      </div>

      {/* Summary chips */}
      <div
        className="d-flex flex-wrap"
        style={{ gap: "0.6rem", marginBottom: "0.8rem" }}
      >
        <span style={summaryChipStyle}>
          <FaRupeeSign size={13} />
          Total Paid : ₹ {formatAmount(totalPaid)}
        </span>
        <span style={summaryChipStyle}>
          <FaReceipt size={13} />
          Payments : {paymentsList.length}
        </span>
      </div>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message>{error?.data?.error || "Error fetching payments"}</Message>
      ) : paymentsList.length === 0 ? (
        <div
          style={{
            ...cardStyle,
            padding: "1.5rem",
            textAlign: "center",
            color: "#6c757d",
          }}
        >
          No payments found for the selected date range
        </div>
      ) : (
        <div style={{ ...cardStyle, overflow: "hidden" }}>
          <Table hover responsive className="mb-0 align-middle">
            <thead>
              <tr>
                <th style={headStyle}>#</th>
                <th style={headStyle}>Invoice</th>
                <th style={headStyle}>Paid Amount</th>
                <th style={headStyle}>Payment Date</th>
                <th style={headStyle}>Payment Mode</th>
                <th style={headStyle}>Reference</th>
                <th style={{ ...headStyle, textAlign: "center" }}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {paymentsList.map((payment, index) => (
                <tr key={payment._id}>
                  <td style={{ ...cellStyle, color: "#6c757d" }}>
                    {index + 1}
                  </td>
                  <td style={{ ...cellStyle, fontWeight: "600" }}>
                    {payment.invoiceNumber ? (
                      <a
                        style={{
                          cursor: "pointer",
                          color: "#1d5f91",
                          textDecoration: "underline",
                          whiteSpace: "nowrap",
                          opacity:
                            openingInvoice === payment.invoiceNumber ? 0.6 : 1,
                        }}
                        onClick={() => handleInvoiceClick(payment.invoiceNumber)}
                      >
                        {payment.invoiceNumber}
                        {openingInvoice === payment.invoiceNumber && (
                          <small className="text-muted"> opening…</small>
                        )}
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td style={cellStyle}>
                    <strong style={{ color: "#1e7e34" }}>
                      ₹ {formatAmount(payment.paidAmount)}
                    </strong>
                  </td>
                  <td style={cellStyle}>
                    {payment.paymentDate?.split("T")[0]}
                  </td>
                  <td style={cellStyle}>
                    <span
                      style={{
                        padding: "0.15rem 0.6rem",
                        borderRadius: "999px",
                        backgroundColor: "#eef6fc",
                        border: "1px solid #d6eaf8",
                        color: "#1d5f91",
                        fontWeight: "600",
                        fontSize: "0.78rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {payment.paymentMode}
                    </span>
                  </td>
                  <td
                    style={{
                      ...cellStyle,
                      color: "#6c757d",
                      maxWidth: "220px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={payment.paymentReference}
                  >
                    {payment.paymentReference || "-"}
                  </td>
                  <td style={{ ...cellStyle, textAlign: "center" }}>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      style={{ borderRadius: "8px" }}
                      onClick={() => setEditingPayment(payment)}
                    >
                      <FaRegEdit style={{ marginBottom: "2px" }} /> Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {editingPayment && (
        <PaymentModal
          show={Boolean(editingPayment)}
          onHide={() => setEditingPayment(null)}
          payment={editingPayment}
          onSuccess={() => refetch()}
        />
      )}
    </div>
  );
};

export default PaymentsHistoryScreen;
